import React, { useState, memo } from "react";
import { MakePayment } from "../../redux/actions/payments";
import { connect } from "react-redux";
import { Validation } from "../../helpers/validations";
import { errors, spanishErrors } from "./constants";
import WrapperComponent from "../wrapperComponent";
import PaymentAddress from "./paymentAddress";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import eCheckImage from "../../assets/img/echeck-sample.jpeg";

const ECHCForm = memo(
  ({
    MakePayment,
    loading,
    user = {},
    setIsCompleted,
    setIsFailed,
    setOpenPaymentAlert,
    closeModal,
    setTransactionResponse,
    resfreshData,
    setRefreshData,
  }) => {
    const [name, setName] = useState("");
    const [amount, setAmount] = useState("");
    const [checkAccount, setCheckAccount] = useState("");
    const [confirmCheckAccount, setConfirmCheckAccount] = useState("");
    const [checkAba, setCheckAba] = useState("");
    const [error, setError] = useState({});
    const [isNextStep, setIsNextStep] = useState(false);
    const [modalECheckImage, setModalECheckImage] = useState(false);
    // Add state for showing/hiding account numbers
    const [showCheckAccount, setShowCheckAccount] = useState(false);
    const [showConfirmAccount, setShowConfirmAccount] = useState(false);

    const [address, setAddress] = useState({
      firstname: "",
      lastname: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
    });

    const { t } = useTranslation();

    const onCompletion = (data) => {
      try {
        if (data?.data?.response_code) {
          // New server: { data: { response_code, response_text, ... }, message }
          setIsFailed(data.data.response_code !== '100');
          setTransactionResponse(data.data.response_text);
          setRefreshData && setRefreshData(!resfreshData);
          setIsCompleted(true);
          setOpenPaymentAlert(true);
        } else {
          setIsFailed(true);
          setTransactionResponse(data?.message || data?.error);
          setOpenPaymentAlert(true);
        }
      } catch (error) {
        console.log(error, "error");
        console.error(error);
      }
    };

    // callback function on successful submission
    const handleSubmit = (e) => {
      e.preventDefault();
      let fields = [
        "amount",
        "checkAccount",
        "confirmCheckAccount",
        "checkAba",
      ];
      let isvalid = false;
      for (let i = 0; i < fields.length; i++) {
        isvalid = handleValidation(fields[i]);
        if (!isvalid) break;
      }
      if (!isvalid) return;

      let payload = {
        checkaccount: checkAccount,
        checkaba: checkAba,
        // checkname: name,
        amount: parseFloat(amount).toFixed(2),
        first_name: address.firstname,
        last_name: address.lastname,
        address1: address.addressLine1,
        address2: address.addressLine2,
        city: address.city,
        state: address.state,
        zip: address.postalCode,
        country: "US",
        leaseId: user.activeLease || "",
        peplId: user.zzID || "",
        email: user.Email_01,
        type: "eCheck",
      };

      MakePayment(payload, onCompletion);
    };

    // validator function
    const handleValidation = (type) => {
      let lang = localStorage.getItem("i18nextLng") === "es" ? true : false;

      // if (type === "name") {
      //   if (!name || !Validation.name(name)) {
      //     setError({ ...error, name: lang ? spanishErrors.name : errors.name });
      //     return false;
      //   }
      // } else
      if (type === "amount") {
        if (!amount || isNaN(parseFloat(amount))) {
          setError({
            ...error,
            amount: lang ? `${spanishErrors.amount}0.01` : `${errors.amount}0.01`,
          });
          return false;
        }
        if (parseFloat(amount) <= 0) {
          setError({
            ...error,
            amount: lang
              ? `${spanishErrors.amount}0.01`
              : `${errors.amount}0.01`,
          });
          return false;
        }
      } else if (type === "checkAccount") {
        if (!checkAccount || checkAccount.length < 8) {
          setError({
            ...error,
            checkAccount: lang
              ? spanishErrors.checkAccount
              : errors.checkAccount,
          });
          return false;
        }
      } else if (type === "confirmCheckAccount") {
        if (!confirmCheckAccount || confirmCheckAccount.length < 8) {
          setError({
            ...error,
            confirmCheckAccount: lang
              ? spanishErrors.checkAccount
              : errors.checkAccount,
          });
          return false;
        }
        if (checkAccount !== confirmCheckAccount) {
          setError({
            ...error,
            confirmCheckAccount: lang
              ? spanishErrors.confirmCheckAccount
              : errors.confirmCheckAccount,
          });
          return false;
        }
      } else if (type === "checkAba") {
        if (!checkAba || checkAba.length !== 9) {
          setError({
            ...error,
            checkAba: lang ? spanishErrors.checkAba : errors.checkAba,
          });
          return false;
        }
      }

      // Clear error for this field if validation passes
      setError({ ...error, [type]: "" });
      return true;
    };

    const handleAmountChange = (value) => {
      // Remove any non-numeric characters except decimal point
      const cleanValue = value.replace(/[^0-9.]/g, "");

      // Handle multiple decimal points - only keep the first one
      const decimalIndex = cleanValue.indexOf(".");
      if (decimalIndex !== -1) {
        const beforeDecimal = cleanValue.substring(0, decimalIndex);
        const afterDecimal = cleanValue.substring(decimalIndex + 1).replace(/\./g, ""); // Remove any additional decimal points
        const limitedAfterDecimal = afterDecimal.slice(0, 2); // Limit to 2 decimal places
        
        setAmount(beforeDecimal + "." + limitedAfterDecimal);
      } else {
        setAmount(cleanValue);
      }
    };

    const handleNextStep = () => {
      let fields = [
        "amount",
        "checkAccount",
        "confirmCheckAccount",
        "checkAba",
      ];
      let isvalid = false;
      for (let i = 0; i < fields.length; i++) {
        isvalid = handleValidation(fields[i]);
        if (!isvalid) break;
      }
      if (isvalid) {
        setIsNextStep(!isNextStep);
      }
    };

    const clearError = (field) => {
      setError({ ...error, [field]: "" });
    };

    const handleConfirmAccountChange = (value) => {
      const cleanValue = value.replace(/[^0-9]/g, "");
      setConfirmCheckAccount(cleanValue);

      if (error.confirmCheckAccount) {
        clearError("confirmCheckAccount");
      }

      if (cleanValue.length >= 8 && checkAccount.length >= 8) {
        if (cleanValue !== checkAccount) {
          setError({
            ...error,
            confirmCheckAccount:
              localStorage.getItem("i18nextLng") === "es"
                ? spanishErrors.confirmCheckAccount
                : errors.confirmCheckAccount,
          });
        }
      }
    };

    // Helper function to mask account number with asterisks
    const maskAccountNumber = (accountNumber) => {
      return "*".repeat(accountNumber.length);
    };

    const renderECheckImageModal = () => {
      return (
        <Modal
          size="md"
          centered
          isOpen={modalECheckImage}
          toggle={() => setModalECheckImage(false)}
        >
          <ModalHeader>eCheck Image</ModalHeader>
          <ModalBody>
            <img src={eCheckImage} alt="eCheck Image" className="img-fluid" />
          </ModalBody>
          <ModalFooter>
            <button
              className="btn btn-primary"
              onClick={() => setModalECheckImage(false)}
            >
              Close
            </button>
          </ModalFooter>
        </Modal>
      );
    };

    return (
      <section className="paymentform-container">
        {!isNextStep ? (
          <>
            <div className="note bg-lime-transparent-5 note-with-right-icon">
              <div className="note-icon">
                <i className="fa fa-university"></i>
              </div>
              <div className="note-content text-right">
                <p>
                  {t("modal.payment.info-text")} <br />
                  {t("modal.payment.stay-tuned")}
                </p>
              </div>
            </div>
            <h4>{t("modal.payment.start-text")}</h4>
            <h5 className="d-flex justify-content-between">
              <b>
                ${" "}
                {user?.leaseDetails?.Rent_Monthly_chargeTenant
                  ? parseFloat(
                      user.leaseDetails.Rent_Monthly_chargeTenant
                    ).toFixed(2)
                  : "0.00"}
              </b>
              <b>
                <a
                  href="javascript:void(0)"
                  className="text-decoration-none"
                  onClick={() => setModalECheckImage(true)}
                >
                  Where to find these details
                </a>
              </b>
            </h5>
            <div className="form-group m-b-10">
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    <i className="fa fa-dollar-sign" />
                  </span>
                </div>
                <input
                  type="text"
                  className={`form-control form-control-lg ${
                    error.amount && "is-invalid"
                  }`}
                  placeholder={t("modal.payment.enter-amount")}
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  onBlur={() => handleValidation("amount")}
                  onFocus={() => clearError("amount")}
                />
              </div>
              {error.amount && (
                <div className="invalid-feedback d-block">{error.amount}</div>
              )}
            </div>
            {/* <div className="form-group m-b-10">
              <input
                type="text"
                className={`form-control form-control-lg ${
                  error.name && "is-invalid"
                }`}
                placeholder={t('modal.payment.account-holder-name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleValidation("name")}
                onFocus={() => clearError("name")}
              />
              {error.name && <div className="invalid-feedback d-block">{error.name}</div>}
            </div> */}
            <div className="form-group m-b-10">
              <input
                type="text"
                className={`form-control form-control-lg ${
                  error.checkAba && "is-invalid"
                }`}
                placeholder={t("modal.payment.aba-routing-number")}
                value={checkAba}
                onChange={(e) =>
                  setCheckAba(e.target.value.replace(/[^0-9]/g, "").slice(0, 9))
                }
                onBlur={() => handleValidation("checkAba")}
                onFocus={() => clearError("checkAba")}
                maxLength="9"
              />
              <p className="text-muted">
                This is a 9-digit number found at the bottom left of your check.
              </p>
              {error.checkAba && (
                <div className="invalid-feedback d-block">{error.checkAba}</div>
              )}
            </div>
            <div className="form-group m-b-10">
              <input
                type="text"
                className={`form-control form-control-lg ${
                  error.checkAccount && "is-invalid"
                }`}
                placeholder={t("modal.payment.account-number")}
                value={
                  showCheckAccount
                    ? checkAccount
                    : maskAccountNumber(checkAccount)
                }
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/[^0-9]/g, "");
                  setCheckAccount(cleanValue);
                  if (error.confirmCheckAccount) {
                    clearError("confirmCheckAccount");
                  }
                  if (
                    cleanValue.length >= 8 &&
                    confirmCheckAccount.length >= 8
                  ) {
                    if (cleanValue !== confirmCheckAccount) {
                      setError({
                        ...error,
                        confirmCheckAccount:
                          localStorage.getItem("i18nextLng") === "es"
                            ? spanishErrors.confirmCheckAccount
                            : errors.confirmCheckAccount,
                      });
                    }
                  }
                }}
                onBlur={() => {
                  handleValidation("checkAccount");
                  setShowCheckAccount(false);
                }}
                onFocus={() => {
                  clearError("checkAccount");
                  setShowCheckAccount(true);
                }}
                maxLength="17"
              />
              <p className="text-muted">
                This number is to the right of the routing number on your check.
                Do not enter the check number
              </p>
              {error.checkAccount && (
                <div className="invalid-feedback d-block">
                  {error.checkAccount}
                </div>
              )}
            </div>
            <div className="form-group m-b-10">
              <input
                type="text"
                className={`form-control form-control-lg ${
                  error.confirmCheckAccount && "is-invalid"
                }`}
                placeholder={t("modal.payment.confirm-account-number")}
                value={
                  showConfirmAccount
                    ? confirmCheckAccount
                    : maskAccountNumber(confirmCheckAccount)
                }
                onChange={(e) => handleConfirmAccountChange(e.target.value)}
                onBlur={() => {
                  handleValidation("confirmCheckAccount");
                  setShowConfirmAccount(false);
                }}
                onFocus={() => {
                  clearError("confirmCheckAccount");
                  setShowConfirmAccount(true);
                }}
                maxLength="17"
              />
              {error.confirmCheckAccount && (
                <div className="invalid-feedback d-block">
                  {error.confirmCheckAccount}
                </div>
              )}
            </div>
            <button
              type="button"
              disabled={loading}
              className="btn btn-warning btn-block"
              onClick={handleNextStep}
            >
              {!loading ? (
                t("modal.payment.next")
              ) : (
                <i className="fas fa-spinner fa-pulse"></i>
              )}
            </button>
          </>
        ) : (
          <PaymentAddress
            handleSubmit={handleSubmit}
            address={address}
            setAddress={setAddress}
            handleNextStep={handleNextStep}
            loading={loading}
          />
        )}

        {renderECheckImageModal()}
      </section>
    );
  }
);

const mapStateToProps = ({ PAYMENT_REDUCER, USER_REDUCER }) => {
  return { loading: PAYMENT_REDUCER.loading, user: USER_REDUCER.userData };
};

const ECHCWrapper = (props) => (
  <WrapperComponent>
    <ECHCForm {...props} />
  </WrapperComponent>
);

export default connect(mapStateToProps, { MakePayment })(ECHCWrapper);
